import { useMutation } from '@tanstack/react-query';
import { requestResetPassword } from '@/features/auth/services/auth';
import type {
  ResetPasswordRequestRequest,
  ResetPasswordRequestResponse,
} from '@/features/auth/types/auth.types';

interface UseRequestResetPasswordOptions {
  onSuccess?: (data: ResetPasswordRequestResponse) => void;
  onError?: (error: Error, variables: ResetPasswordRequestRequest) => void;
}

/**
 * Hook for requesting password reset OTP
 * Sends an OTP token to the user's email
 *
 * @param options - Callbacks for success and error handling
 * @example
 * ```tsx
 * const { mutate: requestReset, isPending } = useRequestResetPassword({
 *   onSuccess: (data) => {
 *     console.log('OTP sent successfully:', data.message);
 *   },
 *   onError: (error) => {
 *     console.error('Failed to send OTP:', error);
 *   }
 * });
 *
 * requestReset({ email: 'user@example.com' });
 * ```
 */
export function useRequestResetPassword(
  options?: UseRequestResetPasswordOptions
) {
  return useMutation<
    ResetPasswordRequestResponse,
    Error,
    ResetPasswordRequestRequest
  >({
    mutationFn: (payload) => requestResetPassword(payload),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error, variables) => {
      options?.onError?.(error, variables);
    },
  });
}
